import { formatMoney } from '@theme/money-formatting';
import { addLinesToCart } from '@theme/kb-cart-add';

/**
 * @typedef {object} KbPdpVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {boolean} available
 * @property {string[]} [options]
 * @property {string} [finish]
 * @property {string} [role] - barrel | cap, on a part rather than a sellable variant
 * @property {KbPdpVariant[]} [parts] - the hardware this finish is made of
 */

/**
 * @typedef {object} KbPdpCartridge
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {number} price
 * @property {KbPdpVariant[]} variants
 * @property {Array<{ name: string }>} shades
 */

/**
 * @typedef {object} KbPdpData
 * @property {number} productId
 * @property {string} productKind
 * @property {string} penCartShape - assembly | pen_parent | barrel_parent | no_parent
 * @property {number|null} selectedVariantId
 * @property {string} currency
 * @property {string} moneyFormat
 * @property {{ addToCart: string, soldOut: string, unavailable: string }} labels
 * @property {KbPdpVariant[]} variants
 * @property {KbPdpCartridge[]} cartridges
 */

/**
 * @typedef {object} KbPdpSelection
 * @property {string} kind
 * @property {KbPdpVariant|null} [penVariant]
 * @property {number|string|null} [cartridgeProductId]
 * @property {KbPdpVariant|null} [cartridgeVariant]
 * @property {KbPdpVariant|null} [variant]
 * @property {string|null} [shadeName]
 * @property {string|null} [finish]
 * @property {number} price
 * @property {string} priceFormatted
 * @property {boolean} available
 * @property {string} [addLabel]
 * @property {string|null} [pendingChoice] - cartridge | shade, the pick a pen still needs before it can be added
 * @property {string} shadeStatus
 * @property {string} source
 * @property {string|null} [changed]
 */

class KbPdpProduct extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      dataScript: 'script[data-kb-pdp-data]',
      parentIdInput: '[data-kb-parent-id]',
      childParentIdInput: '[data-kb-child-parent-id]',
      cartridgeSelect: '[data-kb-cartridge-select]',
      partInput: '[data-kb-component-id]',
      addButton: '[data-kb-pdp-add]',
    };
    this.requiredPartRoles = ['barrel', 'cap'];
    this.partsShapes = ['pen_parent', 'barrel_parent', 'no_parent'];
    this.eventNames = {
      optionChange: 'kb:option:change',
      variantChange: 'kb:variant:change',
    };
    /**
     * @type {{
     *   data: KbPdpData|null,
     *   kind: string|null,
     *   penVariantId: number|string|null,
     *   cartridgeProductId: number|string|null,
     *   shadeName: string|null,
     *   cartridgeVariantId: number|string|null,
     *   variantId: number|string|null
     * }}
     */
    this.state = {
      data: null,
      kind: null,
      penVariantId: null,
      cartridgeProductId: null,
      shadeName: null,
      cartridgeVariantId: null,
      variantId: null,
    };
    /** @type {KbPdpSelection|null} */
    this.currentSelection = null;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    const dataScript = this.querySelector(this.selectors.dataScript);
    if (!dataScript) return;

    try {
      this.state.data = /** @type {KbPdpData} */ (JSON.parse(dataScript.textContent || 'null'));
    } catch (error) {
      this.state.data = null;
    }
    if (!this.state.data) return;

    this.state.kind = this.state.data.productKind;
    this.seedSelection();
    this.resolveSelection('init');

    this.addEventListener(this.eventNames.optionChange, this.handleOptionChange, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /** @returns {KbPdpData} */
  get data() {
    return /** @type {KbPdpData} */ (this.state.data);
  }

  /** @returns {KbPdpSelection|null} */
  get selection() {
    return this.currentSelection;
  }

  /** @returns {string} */
  get sourceId() {
    return this.id || `kb-pdp-${this.dataset.sectionId || this.data.productId}`;
  }

  /**
   * The product page opens a pen with no cartridge or shade picked, so the shopper makes
   * both choices themselves. The shades drawer away from the product page leaves this on:
   * it is for browsing, and opening on the first cartridge's shades is more use there.
   *
   * @returns {boolean}
   */
  get preselects() {
    return this.dataset.preselect !== 'false';
  }

  seedSelection() {
    const data = this.data;
    if (this.state.kind === 'pen') {
      this.state.penVariantId = data.selectedVariantId ?? this.firstVariantId(data.variants);
      if (!this.preselects) return;
      const firstCartridge = (data.cartridges && data.cartridges[0]) || null;
      this.state.cartridgeProductId = firstCartridge ? firstCartridge.id : null;
      this.state.shadeName = this.firstShadeName(firstCartridge);
      this.state.cartridgeVariantId = this.resolveCartridgeVariantId();
    } else {
      this.state.variantId = data.selectedVariantId ?? this.firstVariantId(data.variants);
    }
  }

  /** @param {Event} event */
  handleOptionChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    switch (detail.optionName) {
      case 'finish':
        this.state.penVariantId = detail.value;
        break;
      case 'cartridgeType': {
        if (this.matchesId(detail.value, this.state.cartridgeProductId)) return;
        this.state.cartridgeProductId = detail.value;
        const cartridge = this.findCartridge(detail.value);
        this.state.shadeName = this.preselects ? this.firstShadeName(cartridge) : null;
        this.state.cartridgeVariantId = this.resolveCartridgeVariantId();
        break;
      }
      case 'shade':
        this.state.shadeName = detail.value;
        this.state.cartridgeVariantId = detail.variantId || this.resolveCartridgeVariantId();
        break;
      case 'variant':
        this.state.variantId = detail.value;
        break;
      default:
        return;
    }
    this.resolveSelection(detail.source || 'buy-box', detail.optionName);
  };

  /**
   * @param {string} source
   * @param {string} [changed]
   */
  resolveSelection(source, changed) {
    if (this.state.kind === 'pen') {
      this.resolvePenSelection(source, changed);
    } else {
      this.resolveSingleSelection(source, changed);
    }
  }

  /**
   * @param {string} source
   * @param {string} [changed]
   */
  resolvePenSelection(source, changed) {
    const penVariant = this.findVariant(this.state.penVariantId);
    const cartridge = this.findCartridge(this.state.cartridgeProductId);
    const cartridgeVariant = cartridge
      ? this.findVariantIn(cartridge.variants, this.state.cartridgeVariantId)
      : null;

    const penParts = this.penParts(penVariant);
    const hardwarePrice =
      penParts.length > 0 && this.partsComplete(penParts)
        ? penParts.reduce((total, part) => total + part.price, 0)
        : penVariant
          ? penVariant.price
          : 0;
    const priceMinorUnits = hardwarePrice + this.cartridgePrice(cartridge, cartridgeVariant);
    const hardwareSellable = Boolean(penVariant && penVariant.available) && this.partsSellable(penParts);
    const available = hardwareSellable && Boolean(cartridgeVariant && cartridgeVariant.available);
    const pendingChoice = hardwareSellable ? this.pendingChoice(cartridge) : null;

    this.updatePenFallbackInputs();

    this.currentSelection = {
      kind: 'pen',
      penVariant,
      cartridgeProductId: this.state.cartridgeProductId,
      cartridgeVariant,
      shadeName: this.state.shadeName,
      finish: penVariant ? penVariant.finish || null : null,
      price: priceMinorUnits,
      priceFormatted: this.formatPrice(priceMinorUnits),
      available,
      addLabel: this.penAddLabel(penVariant, cartridgeVariant, hardwareSellable, penParts),
      pendingChoice,
      shadeStatus: pendingChoice ? '' : this.stockStatus(cartridgeVariant),
      source,
      changed: changed || null,
    };

    if (source !== 'init') {
      this.syncUrl(this.state.penVariantId);
      this.dispatchVariantChange();
    }
  }

  /**
   * @param {string} source
   * @param {string} [changed]
   */
  resolveSingleSelection(source, changed) {
    const variant = this.findVariant(this.state.variantId);
    const priceMinorUnits = variant ? variant.price : 0;
    const available = Boolean(variant && variant.available);

    this.currentSelection = {
      kind: this.state.kind || 'accessory',
      variant,
      shadeName: variant ? variant.title : null,
      price: priceMinorUnits,
      priceFormatted: this.formatPrice(priceMinorUnits),
      available,
      shadeStatus: this.stockStatus(variant),
      source,
      changed: changed || null,
    };

    if (source !== 'init') {
      this.syncUrl(this.state.variantId);
      this.dispatchVariantChange();
    }
  }

  updatePenFallbackInputs() {
    const parentInput = /** @type {HTMLInputElement|null} */ (this.querySelector(this.selectors.parentIdInput));
    const cartridgeSelect = /** @type {HTMLSelectElement|null} */ (this.querySelector(this.selectors.cartridgeSelect));
    if (parentInput && this.state.penVariantId != null) parentInput.value = String(this.state.penVariantId);
    if (cartridgeSelect && this.state.cartridgeVariantId != null) {
      cartridgeSelect.value = String(this.state.cartridgeVariantId);
    }

    const penVariant = this.findVariant(this.state.penVariantId);
    const penParts = this.penParts(penVariant);
    const partInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll(this.selectors.partInput));
    partInputs.forEach((input) => {
      const part = penParts.find((candidate) => candidate.role === input.dataset.kbComponentRole);
      const group = input.name.match(/^items\[\d+\]/);
      const fields = /** @type {NodeListOf<HTMLInputElement>} */ (
        group ? this.querySelectorAll(`[name^="${group[0]}"]`) : []
      );
      if (group) fields.forEach((field) => { field.disabled = !part; });
      else input.disabled = !part;
      if (part) input.value = String(part.id);
    });

    const parentVariantId = this.parentVariantId(penVariant);
    if (parentVariantId != null) {
      const childParentInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
        this.querySelectorAll(this.selectors.childParentIdInput)
      );
      childParentInputs.forEach((input) => {
        input.value = String(parentVariantId);
      });
    }
  }

  /** @param {Event} [event] */
  async addToCart(event) {
    if (event) event.preventDefault();
    if (this.state.kind !== 'pen') return;

    const penVariantId = this.state.penVariantId;
    const cartridgeVariantId = this.state.cartridgeVariantId;
    if (penVariantId == null || cartridgeVariantId == null) return;

    const addButton = /** @type {HTMLButtonElement|null} */ (this.querySelector(this.selectors.addButton));
    if (addButton && addButton.disabled) return;
    if (addButton) addButton.disabled = true;

    const penVariant = this.findVariant(penVariantId);
    const parentVariantId = this.parentVariantId(penVariant);
    const writesPenLine = !['barrel_parent', 'no_parent'].includes(this.data.penCartShape);

    /** @type {Array<{ id: number, quantity: number, parent_id?: number }>} */
    const items = [];
    /** @param {number|string} id */
    const pushLine = (id) => {
      /** @type {{ id: number, quantity: number, parent_id?: number }} */
      const line = { id: Number(id), quantity: 1 };
      if (parentVariantId != null && String(parentVariantId) !== String(id)) {
        line.parent_id = Number(parentVariantId);
      }
      items.push(line);
    };

    if (writesPenLine) pushLine(penVariantId);
    this.penParts(penVariant).forEach((part) => pushLine(part.id));
    pushLine(cartridgeVariantId);

    try {
      await addLinesToCart(items, {
        dispatcher: this,
        sourceId: this.sourceId,
        source: 'kb-pdp-product',
        productId: String(this.data.productId),
      });
    } finally {
      if (addButton) addButton.disabled = false;
    }
  }

  /**
   * A host away from the product page writes nothing to the address bar: a cartridge
   * variant in ?variant= on the home page would be meaningless to share or bookmark.
   *
   * @param {number|string|null} variantId
   */
  syncUrl(variantId) {
    if (variantId == null) return;
    if (this.dataset.syncUrl === 'false') return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch {
    }
  }

  dispatchVariantChange() {
    this.dispatchEvent(
      new CustomEvent(this.eventNames.variantChange, {
        bubbles: true,
        composed: true,
        detail: this.currentSelection,
      })
    );
  }

  /**
   * @param {number} minorUnits
   * @returns {string}
   */
  formatPrice(minorUnits) {
    const data = this.state.data;
    if (data && data.moneyFormat) {
      return formatMoney(minorUnits, data.moneyFormat, data.currency || 'GBP');
    }
    return '';
  }

  /**
   * @param {KbPdpVariant|null} penVariant
   * @param {KbPdpVariant|null} cartridgeVariant
   * @param {boolean} hardwareSellable
   * @param {KbPdpVariant[]} penParts
   * @returns {string}
   */
  penAddLabel(penVariant, cartridgeVariant, hardwareSellable, penParts) {
    const labels = this.data.labels || { addToCart: '', soldOut: '', unavailable: '' };
    if (!penVariant || !this.partsComplete(penParts)) return labels.unavailable || '';
    if (!hardwareSellable) return labels.soldOut || '';
    if (!cartridgeVariant) return labels.unavailable || '';
    if (!cartridgeVariant.available) return labels.soldOut || '';
    return labels.addToCart || '';
  }

  /**
   * @param {KbPdpCartridge|null} cartridge
   * @returns {string|null}
   */
  pendingChoice(cartridge) {
    if (!cartridge) return 'cartridge';
    if (this.state.shadeName == null) return 'shade';
    return null;
  }

  /**
   * Until a shade is picked the price counts the cheapest cartridge on offer, or on the
   * picked cartridge, so the figure does not drop and then jump once the pen is complete.
   *
   * @param {KbPdpCartridge|null} cartridge
   * @param {KbPdpVariant|null} cartridgeVariant
   * @returns {number}
   */
  cartridgePrice(cartridge, cartridgeVariant) {
    if (cartridgeVariant) return cartridgeVariant.price;
    if (cartridge) return cartridge.price;
    const prices = (this.data.cartridges || []).map((entry) => entry.price);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }

  /**
   * @param {KbPdpVariant|null} variant
   * @returns {string}
   */
  stockStatus(variant) {
    const labels = this.data.labels || { addToCart: '', soldOut: '', unavailable: '' };
    if (!variant) return labels.unavailable || '';
    if (!variant.available) return labels.soldOut || '';
    return '';
  }

  /**
   * @param {KbPdpVariant|null} penVariant
   * @returns {KbPdpVariant[]}
   */
  penParts(penVariant) {
    if (!this.partsShapes.includes(this.data.penCartShape) || !penVariant) return [];
    return penVariant.parts || [];
  }

  /**
   * @param {KbPdpVariant|null} penVariant
   * @returns {number|string|null}
   */
  parentVariantId(penVariant) {
    const shape = this.data.penCartShape;
    if (shape === 'no_parent') return null;
    if (shape === 'barrel_parent') {
      const barrel = this.penParts(penVariant).find((part) => part.role === 'barrel');
      return barrel ? barrel.id : null;
    }
    return this.state.penVariantId;
  }

  /**
   * @param {KbPdpVariant[]} penParts
   * @returns {boolean}
   */
  partsComplete(penParts) {
    if (!this.partsShapes.includes(this.data.penCartShape)) return true;
    return this.requiredPartRoles.every((role) => penParts.some((part) => part.role === role));
  }

  /**
   * @param {KbPdpVariant[]} penParts
   * @returns {boolean}
   */
  partsSellable(penParts) {
    return this.partsComplete(penParts) && penParts.every((part) => part.available);
  }

  /** @returns {number|null} */
  resolveCartridgeVariantId() {
    const cartridge = this.findCartridge(this.state.cartridgeProductId);
    if (!cartridge || !cartridge.variants || cartridge.variants.length === 0) return null;
    if (this.state.shadeName == null && !this.preselects) return null;
    const match = cartridge.variants.find((variant) => variant.title === this.state.shadeName);
    const fallback = cartridge.variants[0];
    return match ? match.id : fallback ? fallback.id : null;
  }

  /**
   * @param {KbPdpVariant[]|undefined} variants
   * @returns {number|null}
   */
  firstVariantId(variants) {
    const first = variants && variants[0];
    return first ? first.id : null;
  }

  /**
   * @param {KbPdpCartridge|null} cartridge
   * @returns {string|null}
   */
  firstShadeName(cartridge) {
    if (!cartridge || !cartridge.shades) return null;
    const first = cartridge.shades[0];
    return first ? first.name : null;
  }

  /**
   * @param {number|string|null} id
   * @returns {KbPdpVariant|null}
   */
  findVariant(id) {
    return this.findVariantIn(this.data.variants, id);
  }

  /**
   * @param {KbPdpVariant[]|undefined} variants
   * @param {number|string|null} id
   * @returns {KbPdpVariant|null}
   */
  findVariantIn(variants, id) {
    if (!variants) return null;
    return variants.find((variant) => this.matchesId(variant.id, id)) || null;
  }

  /**
   * @param {number|string|null} id
   * @returns {KbPdpCartridge|null}
   */
  findCartridge(id) {
    const cartridges = this.data.cartridges;
    if (!cartridges) return null;
    return cartridges.find((cartridge) => this.matchesId(cartridge.id, id)) || null;
  }

  /**
   * @param {number|string|null|undefined} first
   * @param {number|string|null|undefined} second
   * @returns {boolean}
   */
  matchesId(first, second) {
    return first != null && second != null && String(first) === String(second);
  }
}

if (!customElements.get('kb-pdp-product')) {
  customElements.define('kb-pdp-product', KbPdpProduct);
}

export { KbPdpProduct };
